// InfoReviewCard Component - Display extracted information for review

import { useState } from 'react';
import type { ExtractedInfo, ItemApprovalStatus } from '../../types/info';
import { formatDate } from '../../utils/dateFormatters';
import styles from './InfoReviewCard.module.css';

interface InfoReviewCardProps {
  item: ExtractedInfo;
  index: number;
  totalItems: number;
  approvalStatus: ItemApprovalStatus;
  onApprove: () => void;
  onFeedback: (feedback: string) => void;
  isProcessing?: boolean;
}

export function InfoReviewCard({
  item,
  index,
  totalItems,
  approvalStatus,
  onApprove,
  onFeedback,
  isProcessing = false,
}: InfoReviewCardProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  const handleSubmitFeedback = () => {
    if (feedbackText.trim()) {
      onFeedback(feedbackText.trim());
      setFeedbackText('');
      setShowFeedback(false);
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return styles.confidenceHigh;
    if (confidence >= 0.5) return styles.confidenceMedium;
    return styles.confidenceLow;
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardIndicator}>
          Item {index + 1} of {totalItems}
        </span>
        {approvalStatus !== 'pending' && (
          <span
            className={`${styles.statusBadge} ${
              approvalStatus === 'approved' ? styles.approved : styles.rejected
            }`}
          >
            {approvalStatus}
          </span>
        )}
      </div>

      <div className={styles.cardBody}>
        {/* Summary */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Summary</div>
          <p className={styles.summary}>{item.summary}</p>
        </div>

        {/* Temporal Info */}
        {item.is_temporal && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Temporal Info</div>
            <div className={styles.tagsRow}>
              {item.temporal_date && (
                <span className={`${styles.tag} ${styles.tagTemporal}`}>
                  {formatDate(item.temporal_date)}
                </span>
              )}
              {item.temporal_description && (
                <span className={`${styles.tag} ${styles.tagTemporal}`}>
                  {item.temporal_description}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Categories */}
        {item.categories.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Categories</div>
            <div className={styles.tagsRow}>
              {item.categories.map((cat, i) => (
                <span key={i} className={`${styles.tag} ${styles.tagCategory}`}>
                  {cat}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {item.tags.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Tags</div>
            <div className={styles.tagsRow}>
              {item.tags.map((tag, i) => (
                <span key={i} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Entities */}
        {item.entities.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Entities</div>
            <div className={styles.entitiesList}>
              {item.entities.map((entity, i) => (
                <div key={i} className={styles.entityItem}>
                  <span className={styles.entityIcon}>
                    {entity.entity_type === 'person' && '👤'}
                    {entity.entity_type === 'company' && '🏢'}
                    {entity.entity_type === 'product' && '📦'}
                    {entity.entity_type === 'location' && '📍'}
                    {entity.entity_type === 'date' && '📅'}
                    {entity.entity_type === 'other' && '•'}
                  </span>
                  <span className={styles.entityName}>{entity.name}</span>
                  <span className={styles.entityType}>({entity.entity_type})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Confidence</div>
          <div className={styles.confidenceRow}>
            <div className={styles.confidenceBar}>
              <div
                className={`${styles.confidenceFill} ${getConfidenceColor(item.confidence)}`}
                style={{ width: `${item.confidence * 100}%` }}
              />
            </div>
            <span className={`${styles.confidenceValue} ${getConfidenceColor(item.confidence)}`}>
              {Math.round(item.confidence * 100)}%
            </span>
          </div>
        </div>

        {/* Extraction Notes */}
        {item.extraction_notes && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Notes</div>
            <p className={styles.notes}>{item.extraction_notes}</p>
          </div>
        )}
      </div>

      {/* Feedback Panel */}
      {showFeedback && (
        <div className={styles.feedbackPanel}>
          <div className={styles.feedbackTitle}>Provide Feedback</div>
          <textarea
            className={styles.feedbackTextarea}
            placeholder="Describe what needs to be corrected..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={3}
          />
          <div className={styles.feedbackActions}>
            <button
              className={styles.btnSubmitFeedback}
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim() || isProcessing}
            >
              Submit Feedback
            </button>
            <button
              className={styles.btnCancelFeedback}
              onClick={() => {
                setShowFeedback(false);
                setFeedbackText('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.cardActions}>
        <button
          className={styles.btnFeedback}
          onClick={() => setShowFeedback(!showFeedback)}
          disabled={isProcessing || approvalStatus === 'approved'}
        >
          ✏️ Feedback
        </button>
        <button
          className={styles.btnApprove}
          onClick={onApprove}
          disabled={isProcessing || approvalStatus === 'approved'}
        >
          ✓ Approve
        </button>
      </div>
    </div>
  );
}

export default InfoReviewCard;
